import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import { Box, Chip, Stack, TextField, Typography } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import {
  DataGrid,
  DataGridProps,
  getGridDateOperators,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarProps,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { merge } from 'lodash';
import React from 'react';

import { DataGridPagination } from 'components/atoms/DataGridPagination';
import { toDate } from 'utils/dateTime';
import { useDataGridStyle } from './Style';

export const DEFAULT_READ_ONLY_GRID_FILTER_MODEL: GridFilterModel = { items: [] };
type ReadOnlyDataGridAccentColor = 'primary' | 'secondary';

export const hasActiveReadOnlyGridFilters = (filterModel: GridFilterModel) =>
  Boolean(
    filterModel.quickFilterValues?.some((value) => String(value ?? '').trim().length > 0) ||
      filterModel.items.some((item) => {
        if (!item.field) {
          return false;
        }

        if (item.value == null) {
          return Boolean(item.operator);
        }

        return String(item.value).trim().length > 0;
      })
  );

type ReadOnlyDateTimeRangeValue = [Date | null, Date | null];

const READ_ONLY_DATE_TIME_INPUT_TYPE = 'datetime-local';
const READ_ONLY_DATE_TIME_INPUT_MAX = '9999-12-31T23:59';
const READ_ONLY_DATE_TIME_INPUT_STEP_SECONDS = 60;

const toDateTimeLocalInputValue = (value: unknown) => {
  const parsedDate = toDate(value as string | number | Date | null | undefined);

  if (!parsedDate) {
    return '';
  }

  const localizedDate = new Date(parsedDate);
  localizedDate.setMinutes(localizedDate.getMinutes() - localizedDate.getTimezoneOffset());

  return localizedDate.toISOString().slice(0, 16);
};

const parseDateTimeLocalInputValue = (value: string) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const normalizeDateTimeRangeValue = (value: unknown): ReadOnlyDateTimeRangeValue => {
  if (!Array.isArray(value)) {
    return [null, null];
  }

  return [
    toDate(value[0] as string | number | Date | null | undefined),
    toDate(value[1] as string | number | Date | null | undefined),
  ];
};

const getNormalizedDateTimeRange = (value: unknown): [Date, Date] | null => {
  const [startDate, endDate] = normalizeDateTimeRangeValue(value);

  if (!startDate || !endDate) {
    return null;
  }

  return startDate.getTime() <= endDate.getTime() ? [startDate, endDate] : [endDate, startDate];
};

const ReadOnlyDateTimeRangeFilterInput: React.FC<GridFilterInputValueProps & { disabled?: boolean }> = ({
  item,
  applyValue,
  focusElementRef,
  disabled,
  size,
  variant,
}) => {
  const [startDate, endDate] = normalizeDateTimeRangeValue(item.value);
  const [startInputValue, setStartInputValue] = React.useState(() => toDateTimeLocalInputValue(startDate));
  const [endInputValue, setEndInputValue] = React.useState(() => toDateTimeLocalInputValue(endDate));

  React.useEffect(() => {
    const [nextStartDate, nextEndDate] = normalizeDateTimeRangeValue(item.value);
    setStartInputValue(toDateTimeLocalInputValue(nextStartDate));
    setEndInputValue(toDateTimeLocalInputValue(nextEndDate));
  }, [item.value]);

  const applyRangeValue = React.useCallback(
    (nextStartInputValue: string, nextEndInputValue: string) => {
      const nextStartDate = parseDateTimeLocalInputValue(nextStartInputValue);
      const nextEndDate = parseDateTimeLocalInputValue(nextEndInputValue);

      if (!nextStartInputValue && !nextEndInputValue) {
        applyValue({
          ...item,
          value: undefined,
        });

        return;
      }

      if (!nextStartDate || !nextEndDate) {
        applyValue({
          ...item,
          value: undefined,
        });

        return;
      }

      applyValue({
        ...item,
        value: [nextStartDate, nextEndDate],
      });
    },
    [applyValue, item]
  );

  const handleStartChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextStartInputValue = event.target.value;
      setStartInputValue(nextStartInputValue);
      applyRangeValue(nextStartInputValue, endInputValue);
    },
    [applyRangeValue, endInputValue]
  );

  const handleEndChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextEndInputValue = event.target.value;
      setEndInputValue(nextEndInputValue);
      applyRangeValue(startInputValue, nextEndInputValue);
    },
    [applyRangeValue, startInputValue]
  );

  return (
    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        label="Start"
        variant={variant || 'standard'}
        size={size}
        type={READ_ONLY_DATE_TIME_INPUT_TYPE}
        value={startInputValue}
        onChange={handleStartChange}
        inputRef={focusElementRef}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          max: READ_ONLY_DATE_TIME_INPUT_MAX,
          step: READ_ONLY_DATE_TIME_INPUT_STEP_SECONDS,
        }}
      />
      <TextField
        fullWidth
        label="End"
        variant={variant || 'standard'}
        size={size}
        type={READ_ONLY_DATE_TIME_INPUT_TYPE}
        value={endInputValue}
        onChange={handleEndChange}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          max: READ_ONLY_DATE_TIME_INPUT_MAX,
          step: READ_ONLY_DATE_TIME_INPUT_STEP_SECONDS,
        }}
      />
    </Stack>
  );
};

const buildDateTimeBetweenOperator = (): GridFilterOperator<any, unknown> => ({
  value: 'between',
  label: 'Between',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    const dateRange = getNormalizedDateTimeRange(filterItem.value);

    if (!dateRange) {
      return null;
    }

    const [startDate, endDate] = dateRange;

    return (value) => {
      const parsedValue = toDate(value as string | number | Date | null | undefined);

      if (!parsedValue) {
        return false;
      }

      const timestamp = parsedValue.getTime();

      return timestamp >= startDate.getTime() && timestamp <= endDate.getTime();
    };
  },
  InputComponent: ReadOnlyDateTimeRangeFilterInput,
  requiresFilterValue: true,
});

export const getReadOnlyDateTimeFilterOperators = (): GridFilterOperator<any, unknown>[] => {
  const builtInDateOperators = getGridDateOperators(true);
  const operatorByValue = new Map(builtInDateOperators.map((operator) => [operator.value, operator]));
  const preferredOperatorOrder = [
    'before',
    'after',
    'between',
    'onOrBefore',
    'onOrAfter',
    'is',
    'not',
    'isEmpty',
    'isNotEmpty',
  ] as const;

  return preferredOperatorOrder.reduce<GridFilterOperator<any, unknown>[]>((operators, operatorValue) => {
    if (operatorValue === 'between') {
      operators.push(buildDateTimeBetweenOperator());

      return operators;
    }

    const builtInOperator = operatorByValue.get(operatorValue);

    if (builtInOperator) {
      operators.push(builtInOperator);
    }

    return operators;
  }, []);
};

export interface ReadOnlyDataGridToolbarProps extends Partial<GridToolbarProps> {
  hasActiveFilters?: boolean;
  activeFilterColor?: ReadOnlyDataGridAccentColor;
  quickFilterDebounceMs?: number;
  quickFilterPosition?: 'inline' | 'belowFilters';
  quickFilterSize?: 'small' | 'medium';
  showDensitySelector?: boolean;
  toolbarActions?: React.ReactNode;
}

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends Partial<ReadOnlyDataGridToolbarProps> {}
}

const ReadOnlyDataGridToolbar: React.FC<React.PropsWithChildren<ReadOnlyDataGridToolbarProps>> = ({
  hasActiveFilters = false,
  activeFilterColor = 'secondary',
  quickFilterDebounceMs = 250,
  quickFilterPosition = 'inline',
  quickFilterSize = 'small',
  showDensitySelector = true,
  toolbarActions,
  ...props
}) => {
  const quickFilter = (
    <Box
      sx={
        hasActiveFilters
          ? (theme) => ({
              borderRadius: 999,
              px: 0.75,
              py: 0.25,
              bgcolor: alpha(theme.palette[activeFilterColor].main, 0.1),
              border: '1px solid',
              borderColor: alpha(theme.palette[activeFilterColor].main, 0.24),
              '& .MuiInputBase-root, & .MuiInputBase-input, & .MuiSvgIcon-root': {
                color: theme.palette[activeFilterColor].main,
              },
            })
          : undefined
      }
    >
      <GridToolbarQuickFilter debounceMs={quickFilterDebounceMs} size={quickFilterSize} />
    </Box>
  );
  const filterControls = (
    <Stack
      direction="row"
      spacing={0.75}
      flexWrap="wrap"
      useFlexGap
      sx={
        hasActiveFilters
          ? (theme) => ({
              '& .MuiButtonBase-root': {
                color: theme.palette[activeFilterColor].main,
              },
            })
          : undefined
      }
    >
      <GridToolbarFilterButton {...props} />
      {showDensitySelector ? <GridToolbarDensitySelector {...props} /> : null}
      {hasActiveFilters ? (
        <Chip
          icon={<FilterAltRoundedIcon />}
          label="Filters active"
          color={activeFilterColor}
          size="small"
          variant="filled"
        />
      ) : null}
    </Stack>
  );
  const actionControls = toolbarActions ? <Box>{toolbarActions}</Box> : null;

  if (quickFilterPosition === 'belowFilters') {
    return (
      <GridToolbarContainer sx={{ px: 0.5, py: 0.75, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Stack spacing={0.5} alignItems="flex-start">
          {filterControls}
          {quickFilter}
        </Stack>
        {actionControls}
      </GridToolbarContainer>
    );
  }

  return (
    <GridToolbarContainer sx={{ px: 0.5, py: 0.75, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      {filterControls}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
        {actionControls}
        {quickFilter}
      </Stack>
    </GridToolbarContainer>
  );
};

interface ReadOnlyDataGridEmptyStateProps {
  hasActiveFilters: boolean;
  activeFilterColor: ReadOnlyDataGridAccentColor;
  title: string;
  description?: React.ReactNode;
}

const ReadOnlyDataGridEmptyState: React.FC<ReadOnlyDataGridEmptyStateProps> = ({
  hasActiveFilters,
  activeFilterColor,
  title,
  description,
}) => (
  <Stack
    spacing={1.25}
    alignItems="center"
    justifyContent="center"
    sx={(theme) => ({
      height: '100%',
      minHeight: 'var(--DataGrid-overlayHeight, 220px)',
      textAlign: 'center',
      px: 3,
      py: 2,
      bgcolor: alpha(theme.palette.background.paper, 0.97),
      borderRadius: 2,
      border: '1px dashed',
      borderColor: hasActiveFilters ? alpha(theme.palette[activeFilterColor].main, 0.3) : theme.palette.divider,
      backdropFilter: 'blur(6px)',
    })}
  >
    <Box
      sx={(theme) => ({
        width: 44,
        height: 44,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        bgcolor: hasActiveFilters ? alpha(theme.palette[activeFilterColor].main, 0.12) : theme.palette.action.hover,
        color: hasActiveFilters ? theme.palette[activeFilterColor].main : theme.palette.text.secondary,
      })}
    >
      {hasActiveFilters ? <SearchOffRoundedIcon fontSize="small" /> : <TableChartRoundedIcon fontSize="small" />}
    </Box>

    <Stack spacing={0.5} alignItems="center">
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
          {description}
        </Typography>
      ) : null}
    </Stack>

    {hasActiveFilters ? (
      <Chip
        icon={<FilterAltRoundedIcon />}
        label="Filters active"
        color={activeFilterColor}
        size="small"
        variant="filled"
      />
    ) : null}
  </Stack>
);

export interface ReadOnlyDataGridProps extends DataGridProps {
  height?: CSSStyleDeclaration['height'];
  hasActiveFilters?: boolean;
  activeFilterColor?: ReadOnlyDataGridAccentColor;
  noRowsTitle?: string;
  noRowsSubtitle?: React.ReactNode;
  noResultsTitle?: string;
  noResultsSubtitle?: React.ReactNode;
  overlayHeight?: string;
  quickFilterDebounceMs?: number;
  quickFilterPosition?: 'inline' | 'belowFilters';
  quickFilterSize?: 'small' | 'medium';
  showDensitySelector?: boolean;
}

const resolveSxFragment = (
  sxFragment: Exclude<DataGridProps['sx'], undefined>,
  theme: Theme
): Record<string, unknown> => {
  if (typeof sxFragment === 'function') {
    return sxFragment(theme) as Record<string, unknown>;
  }

  return sxFragment as Record<string, unknown>;
};

export const ReadOnlyDataGrid: React.FC<React.PropsWithChildren<ReadOnlyDataGridProps>> = ({
  height = '100%',
  hasActiveFilters,
  activeFilterColor = 'secondary',
  noRowsTitle = 'No records',
  noRowsSubtitle,
  noResultsTitle = 'No results match current filters',
  noResultsSubtitle = 'Try changing or clearing the active filters to see rows again.',
  overlayHeight = 'clamp(180px, 28vh, 260px)',
  quickFilterDebounceMs,
  quickFilterPosition,
  quickFilterSize,
  showDensitySelector,
  slots,
  slotProps,
  sx,
  filterModel,
  ...props
}) => {
  const baseDataGridStyles = useDataGridStyle();
  const resolvedHasActiveFilters = React.useMemo(
    () => hasActiveFilters ?? hasActiveReadOnlyGridFilters(filterModel || DEFAULT_READ_ONLY_GRID_FILTER_MODEL),
    [filterModel, hasActiveFilters]
  );

  const resolvedSlots: DataGridProps['slots'] = React.useMemo(
    () => ({
      pagination: DataGridPagination,
      toolbar: ReadOnlyDataGridToolbar,
      noRowsOverlay: () => (
        <ReadOnlyDataGridEmptyState
          hasActiveFilters={false}
          activeFilterColor={activeFilterColor}
          title={noRowsTitle}
          description={noRowsSubtitle}
        />
      ),
      noResultsOverlay: () => (
        <ReadOnlyDataGridEmptyState
          hasActiveFilters={resolvedHasActiveFilters}
          activeFilterColor={activeFilterColor}
          title={noResultsTitle}
          description={noResultsSubtitle}
        />
      ),
      ...slots,
    }),
    [activeFilterColor, noResultsSubtitle, noResultsTitle, noRowsSubtitle, noRowsTitle, resolvedHasActiveFilters, slots]
  );

  const resolvedSlotProps: ReadOnlyDataGridProps['slotProps'] = React.useMemo(() => {
    const toolbarSlotProps = (slotProps?.toolbar || {}) as Record<string, unknown>;
    const paginationSlotProps = (slotProps?.pagination || {}) as Record<string, unknown>;
    const paginationSelectProps = (paginationSlotProps.SelectProps || {}) as Record<string, unknown>;

    return {
      ...(slotProps || {}),
      toolbar: {
        hasActiveFilters: resolvedHasActiveFilters,
        activeFilterColor,
        quickFilterDebounceMs,
        quickFilterPosition,
        quickFilterSize,
        showDensitySelector,
        ...toolbarSlotProps,
      },
      pagination: {
        ...paginationSlotProps,
        SelectProps: {
          sx: { width: 'auto' },
          ...paginationSelectProps,
        },
      },
    };
  }, [
    activeFilterColor,
    quickFilterDebounceMs,
    quickFilterPosition,
    quickFilterSize,
    resolvedHasActiveFilters,
    showDensitySelector,
    slotProps,
  ]);

  const resolvedSx = React.useMemo(
    () => (theme: Theme) => {
      const sxFragments = [
        baseDataGridStyles,
        {
          '--DataGrid-overlayHeight': overlayHeight,
          border: 'none',
          '& .MuiDataGrid-toolbarContainer': {
            borderBottom: '1px solid',
            borderColor: resolvedHasActiveFilters
              ? alpha(theme.palette[activeFilterColor].main, 0.22)
              : theme.palette.divider,
            bgcolor: resolvedHasActiveFilters ? alpha(theme.palette[activeFilterColor].main, 0.04) : 'transparent',
            mb: 1,
            borderRadius: 1.5,
          },
          '& .MuiDataGrid-overlayWrapper': {
            bgcolor: alpha(theme.palette.background.paper, 0.97),
          },
          ...(resolvedHasActiveFilters
            ? {
                '& .MuiDataGrid-columnHeaders': {
                  borderBottomColor: alpha(theme.palette[activeFilterColor].main, 0.22),
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTopColor: alpha(theme.palette[activeFilterColor].main, 0.22),
                },
              }
            : {}),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ];

      return sxFragments.reduce<Record<string, unknown>>((accumulator, fragment) => {
        if (!fragment) {
          return accumulator;
        }

        return merge(accumulator, resolveSxFragment(fragment, theme));
      }, {});
    },
    [activeFilterColor, baseDataGridStyles, overlayHeight, resolvedHasActiveFilters, sx]
  );

  return (
    <Box height={height} width="100%">
      <DataGrid
        {...props}
        filterModel={filterModel}
        sx={resolvedSx}
        slots={resolvedSlots}
        slotProps={resolvedSlotProps}
      />
    </Box>
  );
};
